import { GUEST_ACCESS_HASH_KEY } from '@event/constants';
import { useAddNonParticipantMutation } from '@event/services/events';
import { EventTypes } from '@event/types/event';
import { handleError, toaster } from '@utils/common';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';

interface Props {
    isAuthenticated?: boolean;
    handleRegistration: () => Promise<void>;
}

const useQuizz = ({ isAuthenticated, handleRegistration }: Props) => {
    const [quizzDialogOpen, setQuizzDialogOpen] = useState(false);
    const [quizzStatus, setQuizzStatus] = useState<{
        guestInfo?: null | {
            first_name: string;
            last_name: string;
            email: string;
            company_name?: string;
        };
        show: boolean;
        inProgress: boolean;
        confirm: boolean;
    }>({
        show: false,
        inProgress: false,
        confirm: false,
    });

    const [_, setSearchParams] = useSearchParams();
    const { type, eventId } = useParams();
    const eventType = type as EventTypes;
    const [addNonParticipant] = useAddNonParticipantMutation();
    const intl = useIntl();

    const handleStarted = useCallback(() => setQuizzStatus((prev) => ({ ...prev, inProgress: true })), []);

    const handleIconClose = useCallback(() => {
        if (quizzStatus.inProgress) {
            setQuizzStatus((prev) => ({ ...prev, show: false, confirm: true }));
        } else {
            setQuizzStatus((prev) => ({ ...prev, show: false, inProgress: false }));
            setQuizzDialogOpen(false);
        }
    }, [quizzStatus.inProgress]);

    const handleClose = useCallback(() => {
        setQuizzDialogOpen(false);
        setQuizzStatus({
            guestInfo: null,
            show: false,
            inProgress: false,
            confirm: false,
        });
    }, []);

    const handleGoBack = useCallback(() => {
        setQuizzStatus((prev) => ({
            ...prev,
            show: true,
            confirm: false,
        }));
    }, []);

    const handleSubmit = useCallback(async () => {
        if (isAuthenticated) {
            setQuizzStatus((prev) => ({ ...prev, inProgress: false }));
            await handleRegistration();
            setTimeout(() => {
                setQuizzDialogOpen(false);
            }, 3000);
        } else if (quizzStatus.guestInfo) {
            try {
                const { guest_access_hash } = await addNonParticipant({
                    id: Number(eventId),
                    type: eventType,
                    ...quizzStatus.guestInfo,
                }).unwrap();
                toaster.success({
                    msg: intl.formatMessage({
                        id: 'event.registered',
                        defaultMessage: 'You are now registered',
                    }),
                });
                setSearchParams((prev) => {
                    const params = new URLSearchParams(prev);
                    params.set(GUEST_ACCESS_HASH_KEY, guest_access_hash);
                    return params;
                });
            } catch (err) {
                handleError(
                    intl.formatMessage({
                        id: 'event.error.registration',
                        defaultMessage: 'Error registering for event',
                    }),
                    err
                );
            } finally {
                setTimeout(() => setQuizzDialogOpen(false), 3000);
            }
        }
        /**
         * Don't want to re-render this function if handleRegistration changes.
         * If so, it'll trigger a re-render on the Widget from Typeform, and that'll reset the view.
         * This happens due to the iframe being re-rendered.
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addNonParticipant, eventId, eventType, quizzStatus.guestInfo, setSearchParams, isAuthenticated]);

    return {
        quizzStatus,
        setQuizzStatus,
        quizzDialogOpen,
        setQuizzDialogOpen,
        handleStarted,
        handleClose,
        handleIconClose,
        handleGoBack,
        handleSubmit,
    };
};

export { useQuizz };
